<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type PropType, type VNode } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  NButton,
  NDropdown,
  NIcon,
  NSpace,
  NTag,
  useDialog,
  type DataTableColumns,
  type DataTableSortState,
  type DropdownOption,
} from 'naive-ui';
import { IconDelete, IconEdit, IconMail, IconMore, IconUserAdd } from '@/components/design/icons';
import {
  OrganisationInvitationStatus,
  type OrganisationInvitationInDb,
  type UserInDb,
} from '@/api';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useAuthStore } from '@/stores/auth';
import { formatDateForDisplay, formatDateTimeForTable } from '@/utils/datetimeUtils';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaRoleSelect from '@/components/design/domain/people/GaiaRoleSelect.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useMemberActions } from '@/composables/useMemberActions';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import { PermissionsService } from '@/services/permissionsService';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import OrganisationSeveranceModal from '@/components/organisation/OrganisationSeveranceModal.vue';
import PeoplePageHeader from '@/components/organisation/PeoplePageHeader.vue';

/**
 * The People page's Members and Invitations tabs. Roles is its own view
 * (PermissionsView); the shared header joins the three into one page.
 */
const props = defineProps({
  tab: {
    type: String as PropType<'members' | 'invitations'>,
    default: 'members',
  },
});

const router = useRouter();
const organizationStore = useOrganizationStore();
const authStore = useAuthStore();
const message = useGaiaToast();
const dialog = useDialog();

const isAdmin = computed(() => authStore.userPermissions.is_org_admin);

// ── Members: one server page at a time ──────────────────────────────────────
// Page, size and search live in the URL like the other list pages. The store's
// `users` list is left alone: reviews use it as a lookup, and it is only the
// first 50 members.
// Email A→Z is the order the list had before sorting existed.
const { currentPage, pageSize, searchQuery, sortKey, sortDirection, sortActivated } =
  useListQuerySync({ defaultSortKey: 'email', defaultSortDirection: 'asc' });
const members = ref<UserInDb[]>([]);
const membersTotal = ref(0);
const membersLoading = ref(false);
const membersPagination = usePaginationConfig(currentPage, pageSize, membersTotal);

async function fetchMembers(): Promise<void> {
  membersLoading.value = true;
  try {
    const result = await PermissionsService.searchUsers({
      text: searchQuery.value.trim(),
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
    });
    // Removing the last member on the last page leaves it empty: step back,
    // and the page watcher fetches it.
    if (result.results.length === 0 && currentPage.value > 1 && result.result_count > 0) {
      currentPage.value = Math.ceil(result.result_count / pageSize.value);
      return;
    }
    members.value = result.results;
    membersTotal.value = result.result_count;
  } catch (error: unknown) {
    message.error(error instanceof Error ? error.message : 'Failed to fetch members');
    logger.error('Failed to fetch members', error);
  } finally {
    membersLoading.value = false;
  }
}

// usePaginationConfig only updates the refs; the fetch follows them.
watch([currentPage, pageSize], () => {
  void fetchMembers();
});

/**
 * The search sorts server-side by any column on the user row. Clearing a sort
 * returns to the default order. Either way the list restarts from page one.
 */
function handleMemberSort(sorter: DataTableSortState | DataTableSortState[] | null): void {
  if (!sorter || Array.isArray(sorter)) return;
  if (sorter.order === false) {
    sortKey.value = 'email';
    sortDirection.value = 'asc';
    sortActivated.value = false;
  } else {
    sortKey.value = String(sorter.columnKey);
    sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
    sortActivated.value = true;
  }
  if (currentPage.value === 1) {
    void fetchMembers();
  } else {
    currentPage.value = 1;
  }
}

function sortOrderFor(key: string): 'ascend' | 'descend' | false {
  if (!sortActivated.value || sortKey.value !== key) return false;
  return sortDirection.value === 'asc' ? 'ascend' : 'descend';
}

// A new search starts from the first page. The field debounces its own
// keystrokes and fires at once when it is cleared.
function applyMemberSearch(): void {
  if (currentPage.value === 1) {
    void fetchMembers();
  } else {
    currentPage.value = 1;
  }
}

function userRoute(row: { uid: string }): RouteLocationRaw {
  return { name: 'user-detail', params: { uid: row.uid } };
}

function tableRowProps(row: { uid: string }): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(userRoute(row));
    },
  };
}

// ── Shared cell renderers ───────────────────────────────────────────────────

/**
 * Name over email — who the row is, in the order people recognise them.
 * With `to`, the name is a real link so cmd- and middle-click open it in a new tab.
 */
function renderPerson(
  fullName: string | null | undefined,
  email: string,
  to?: RouteLocationRaw
): VNode {
  const name = h(
    GaiaText,
    { size: 'body', weight: 'semibold', tone: 'ink' },
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty name falls back too
    () => fullName || email
  );
  return h('div', { style: { display: 'flex', flexDirection: 'column', minWidth: 0 } }, [
    to
      ? h(
          RouterLink,
          {
            to,
            style: 'color: inherit; text-decoration: none',
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
            },
          },
          { default: () => name }
        )
      : name,
    fullName ? h(GaiaText, { size: 'control', tone: 'subtle' }, () => email) : null,
  ]);
}

function menuIcon(icon: typeof IconMore): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

/** The row's "⋯" menu. Clicks stop here so the row doesn't also open the member. */
function renderRowMenu(
  options: DropdownOption[],
  onSelect: (key: string) => void,
  label: string
): VNode {
  return h(
    'span',
    {
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
      },
    },
    [
      h(
        NDropdown,
        {
          trigger: 'click',
          placement: 'bottom-end',
          options,
          onSelect: (key: string | number) => {
            onSelect(String(key));
          },
        },
        {
          default: () =>
            h(
              NButton,
              { quaternary: true, size: 'small', 'aria-label': label, title: label },
              { icon: () => h(NIcon, null, { default: () => h(IconMore) }) }
            ),
        }
      ),
    ]
  );
}

// ── Members ─────────────────────────────────────────────────────────────────

const memberActions = useMemberActions({ onChanged: fetchMembers, onRemoved: fetchMembers });
const { severance, severanceLoading, severanceError, closeSeverance, confirmSeverance } =
  memberActions;

const memberColumns = computed((): DataTableColumns<UserInDb> => [
  {
    title: 'Member',
    key: 'full_name',
    sorter: true,
    sortOrder: sortOrderFor('full_name'),
    render: row => renderPerson(row.full_name, row.email, userRoute(row)),
  },
  {
    title: 'Joined',
    key: 'created_at',
    width: 160,
    sorter: true,
    sortOrder: sortOrderFor('created_at'),
    render: row =>
      h(GaiaText, { size: 'control', tone: 'subtle' }, () => formatDateForDisplay(row.created_at)),
  },
  {
    title: 'Status',
    key: 'is_active',
    width: 140,
    sorter: true,
    sortOrder: sortOrderFor('is_active'),
    render: row =>
      h(
        NTag,
        { type: row.is_active ? 'success' : 'default', size: 'small', bordered: false },
        { default: () => (row.is_active ? 'Active' : 'Deactivated') }
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            memberActions.menuOptions(row),
            key => {
              memberActions.run(row, key);
            },
            `Actions for ${row.email}`
          )
        : h('span'),
  },
]);

// ── Invitations ─────────────────────────────────────────────────────────────

// Accepted invitations already show up as members.
const openInvitations = computed((): OrganisationInvitationInDb[] =>
  organizationStore.invitations.filter(
    invitation => invitation.status !== OrganisationInvitationStatus.ACCEPTED
  )
);

/**
 * The invitations endpoint has no search, but it returns every invitation at
 * once, so the search runs here: name, email or who sent it. The field still
 * debounces, so it behaves like the server-backed searches on the other tabs.
 */
const invitationSearch = ref('');
const invitationFilter = ref('');

const visibleInvitations = computed((): OrganisationInvitationInDb[] => {
  const needle = invitationFilter.value.trim().toLowerCase();
  if (!needle) return openInvitations.value;
  return openInvitations.value.filter(invitation =>
    [invitation.email, invitation.full_name, invitation.invited_by_email].some(value =>
      value?.toLowerCase().includes(needle)
    )
  );
});

const INVITATION_STATUS: Record<
  OrganisationInvitationStatus,
  { label: string; type: 'info' | 'success' | 'warning' | 'default' }
> = {
  [OrganisationInvitationStatus.PENDING]: { label: 'Waiting', type: 'info' },
  [OrganisationInvitationStatus.ACCEPTED]: { label: 'Joined', type: 'success' },
  [OrganisationInvitationStatus.EXPIRED]: { label: 'Expired', type: 'warning' },
  [OrganisationInvitationStatus.REVOKED]: { label: 'Revoked', type: 'default' },
};

function invitationMenuOptions(row: OrganisationInvitationInDb): DropdownOption[] {
  const pending = row.status === OrganisationInvitationStatus.PENDING;
  const options: DropdownOption[] = [];
  // Roles can change only while the invitation is pending; Resend turns an
  // expired one back into a pending one.
  if (pending) options.push({ key: 'roles', label: 'Change roles', icon: menuIcon(IconEdit) });
  if (pending || row.status === OrganisationInvitationStatus.EXPIRED) {
    options.push({ key: 'resend', label: 'Resend invitation', icon: menuIcon(IconMail) });
  }
  if (pending) {
    options.push(
      { type: 'divider', key: 'divider' },
      { key: 'revoke', label: 'Revoke invitation', icon: menuIcon(IconDelete) }
    );
  }
  return options;
}

function onInvitationAction(row: OrganisationInvitationInDb, key: string): void {
  switch (key) {
    case 'roles': {
      openEditRolesModal(row);
      break;
    }
    case 'resend': {
      void handleResendInvitation(row);
      break;
    }
    case 'revoke': {
      dialog.warning({
        title: `Revoke the invitation for ${row.email}?`,
        content: 'The link in their email will stop working. You can invite them again later.',
        positiveText: 'Revoke',
        negativeText: 'Cancel',
        positiveButtonProps: { type: 'error' },
        onPositiveClick: () => handleRevokeInvitation(row),
      });
      break;
    }
    // No default
  }
}

const invitationColumns = computed((): DataTableColumns<OrganisationInvitationInDb> => [
  {
    title: 'Invited',
    key: 'email',
    render: row => renderPerson(row.full_name, row.email),
  },
  {
    title: 'Roles',
    key: 'permission_roles',
    render: row =>
      row.permission_roles.length === 0
        ? h(GaiaText, { size: 'control', tone: 'subtle' }, () => 'Member only')
        : h(
            NSpace,
            { size: [4, 4], inline: true },
            {
              default: () =>
                row.permission_roles.map(role =>
                  h(
                    NTag,
                    { size: 'small', bordered: false, key: role.uid },
                    { default: () => role.name }
                  )
                ),
            }
          ),
  },
  {
    title: 'Invited by',
    key: 'invited_by_email',
    render: row =>
      h(GaiaText, { size: 'control', tone: 'subtle' }, () => row.invited_by_email ?? '—'),
  },
  {
    title: 'Expires',
    key: 'expires_at',
    width: 190,
    render: row =>
      h(GaiaText, { size: 'control', tone: 'subtle' }, () =>
        formatDateTimeForTable(row.expires_at)
      ),
  },
  {
    title: 'Status',
    key: 'status',
    width: 120,
    render: row =>
      h(
        NTag,
        { type: INVITATION_STATUS[row.status].type, size: 'small', bordered: false },
        { default: () => INVITATION_STATUS[row.status].label }
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: (row): VNode => {
      const options = invitationMenuOptions(row);
      return options.length > 0
        ? renderRowMenu(
            options,
            key => {
              onInvitationAction(row, key);
            },
            `Actions for the invitation to ${row.email}`
          )
        : h('span');
    },
  },
]);

// ── Invite dialog ───────────────────────────────────────────────────────────

const showInviteModal = ref(false);
const inviteEmail = ref('');
const inviteFullName = ref('');
const inviteRoleUids = ref<string[]>([]);
const inviteAttempted = ref(false);
const inviteError = ref<string | null>(null);
const inviting = ref(false);

// Deliberately loose: one @ with text on both sides and a dot in the domain.
// The backend is the real validator; this only catches obvious typos early.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emailError(email: string): string | undefined {
  if (!email) return 'Enter their email address';
  return EMAIL_RE.test(email) ? undefined : 'That doesn’t look like an email address';
}

const inviteErrors = computed(() => ({
  email: emailError(inviteEmail.value.trim()),
  fullName: inviteFullName.value.trim() ? undefined : 'Enter their name',
}));

function openInviteModal(): void {
  inviteEmail.value = '';
  inviteFullName.value = '';
  inviteRoleUids.value = [];
  inviteAttempted.value = false;
  inviteError.value = null;
  showInviteModal.value = true;
}

// The backend creates the invitation and sends the mail; the invitee accepts
// via the public /invite/accept page.
async function handleInviteUser(): Promise<void> {
  if (!isAdmin.value) return;
  inviteAttempted.value = true;
  if (inviteErrors.value.email || inviteErrors.value.fullName) return;
  const email = inviteEmail.value.trim();
  inviting.value = true;
  inviteError.value = null;
  const success = await organizationStore.sendInvitation(
    email,
    inviteFullName.value.trim(),
    inviteRoleUids.value
  );
  inviting.value = false;
  if (!success) {
    inviteError.value = organizationStore.error ?? 'Could not send the invitation.';
    return;
  }
  message.success(`Invitation sent to ${email}`);
  showInviteModal.value = false;
  // Show them where the invitation went.
  if (props.tab !== 'invitations') void router.push({ name: 'settings-invitations' });
}

// ── Change invitation roles dialog ──────────────────────────────────────────

const showEditRolesModal = ref(false);
const editRolesInvitation = ref<OrganisationInvitationInDb | null>(null);
const editRoleUids = ref<string[]>([]);
const savingRoles = ref(false);

function openEditRolesModal(invitation: OrganisationInvitationInDb): void {
  editRolesInvitation.value = invitation;
  editRoleUids.value = invitation.permission_roles.map(role => role.uid);
  showEditRolesModal.value = true;
}

// An empty selection clears the extra roles.
async function handleSaveInvitationRoles(): Promise<void> {
  const invitation = editRolesInvitation.value;
  if (!invitation) return;
  savingRoles.value = true;
  const success = await organizationStore.updateInvitationRoleSet(
    invitation.uid,
    editRoleUids.value
  );
  savingRoles.value = false;
  if (success) {
    message.success(`Roles updated for ${invitation.email}`);
    showEditRolesModal.value = false;
    editRolesInvitation.value = null;
  } else if (organizationStore.error) {
    message.error(organizationStore.error);
  }
}

// ── Data ────────────────────────────────────────────────────────────────────

onMounted(async (): Promise<void> => {
  await fetchMembers();
  if (isAdmin.value) {
    await organizationStore.fetchInvitations();
  }
});

async function handleResendInvitation(invitation: OrganisationInvitationInDb): Promise<void> {
  const success = await organizationStore.resendOrganisationInvitation(invitation.uid);
  if (success) {
    message.success(`Invitation resent to ${invitation.email}`);
  } else if (organizationStore.error) {
    message.error(organizationStore.error);
  }
}

async function handleRevokeInvitation(invitation: OrganisationInvitationInDb): Promise<void> {
  const success = await organizationStore.revokeOrganisationInvitation(invitation.uid);
  if (success) {
    message.success(`Invitation for ${invitation.email} revoked`);
  } else if (organizationStore.error) {
    message.error(organizationStore.error);
  }
}
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.people }}</n-breadcrumb-item>
    </template>

    <people-page-header :active="props.tab">
      <template #actions>
        <gaia-search-input
          v-if="props.tab === 'members'"
          v-model:value="searchQuery"
          placeholder="Search members"
          :debounce="500"
          style="width: 220px"
          @search="applyMemberSearch"
        />
        <gaia-search-input
          v-else-if="openInvitations.length > 0"
          v-model:value="invitationSearch"
          placeholder="Search invitations"
          :debounce="250"
          style="width: 220px"
          @search="(value: string) => (invitationFilter = value)"
        />
        <gaia-button v-if="isAdmin" type="primary" @click="openInviteModal">
          <template #icon>
            <n-icon :component="IconUserAdd" />
          </template>
          Invite people
        </gaia-button>
      </template>
    </people-page-header>

    <gaia-table
      v-if="props.tab === 'members'"
      :columns="memberColumns"
      :data="members"
      :loading="membersLoading"
      remote
      :pagination="membersPagination"
      :row-key="(row: UserInDb) => row.uid"
      :row-props="tableRowProps"
      @update:sorter="handleMemberSort"
    >
      <template #empty>
        <n-empty
          v-if="!membersLoading"
          :description="
            searchQuery.trim() ? `No members match “${searchQuery.trim()}”` : 'No members yet'
          "
        />
      </template>
    </gaia-table>

    <template v-else>
      <gaia-table
        v-if="openInvitations.length > 0 || organizationStore.invitationsLoading"
        :columns="invitationColumns"
        :data="visibleInvitations"
        :loading="organizationStore.invitationsLoading"
        :row-key="(row: OrganisationInvitationInDb) => row.uid"
      >
        <template #empty>
          <n-empty :description="`No invitations match “${invitationFilter.trim()}”`" />
        </template>
      </gaia-table>
      <gaia-empty-state v-else title="No invitations waiting" dashed>
        <template #icon>
          <n-icon :component="IconMail" :size="18" />
        </template>
        Invite someone by email and they’ll appear here until they join.
        <template v-if="isAdmin" #actions>
          <gaia-button type="primary" @click="openInviteModal">Invite people</gaia-button>
        </template>
      </gaia-empty-state>
    </template>

    <OrganisationSeveranceModal
      v-if="severance"
      :show="!!severance"
      :mode="severance.mode"
      :organisation-name="organizationStore.activeOrganization?.name ?? ''"
      :user-email="severance.email"
      :loading="severanceLoading"
      :error="severanceError"
      @update:show="open => !open && closeSeverance()"
      @confirm="confirmSeverance"
    />

    <!-- Invite -->
    <gaia-modal
      v-model:show="showInviteModal"
      width="480px"
      title="Invite people"
      :mask-closable="!inviting"
      :close-on-esc="!inviting"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-text size="body" tone="subtle" :style="{ lineHeight: 1.55 }">
          We’ll email them a link to join
          {{ organizationStore.activeOrganization?.name ?? 'your organisation' }}.
        </gaia-text>
        <gaia-banner v-if="inviteError" type="error">{{ inviteError }}</gaia-banner>
        <gaia-form-field
          label="Email"
          required
          :error="inviteAttempted ? inviteErrors.email : undefined"
        >
          <gaia-input
            v-model:value="inviteEmail"
            placeholder="name@company.com"
            :input-props="{ type: 'email', autocomplete: 'off' }"
          />
        </gaia-form-field>
        <gaia-form-field
          label="Full name"
          required
          :error="inviteAttempted ? inviteErrors.fullName : undefined"
        >
          <gaia-input v-model:value="inviteFullName" placeholder="Jane Smith" />
        </gaia-form-field>
        <gaia-form-field label="Extra roles" hint="Optional">
          <gaia-role-select
            v-model:value="inviteRoleUids"
            multiple
            clearable
            exclude-protected-member
            placeholder="Only the Organisation Member role"
          />
          <gaia-text size="control" tone="subtle" :style="{ marginTop: '6px', display: 'block' }">
            Everyone gets the Organisation Member role. Add others if they need more.
          </gaia-text>
        </gaia-form-field>
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="inviting" @click="showInviteModal = false">Cancel</gaia-button>
          <gaia-button type="primary" :loading="inviting" @click="handleInviteUser">
            Send invitation
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Change an invitation's roles -->
    <gaia-modal
      v-model:show="showEditRolesModal"
      width="480px"
      title="Change roles"
      :mask-closable="!savingRoles"
      :close-on-esc="!savingRoles"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-text size="body" tone="subtle" :style="{ lineHeight: 1.55 }">
          The roles {{ editRolesInvitation?.email }} gets on joining. Everyone also gets the
          Organisation Member role.
        </gaia-text>
        <gaia-form-field label="Extra roles" hint="Optional">
          <gaia-role-select
            v-model:value="editRoleUids"
            multiple
            clearable
            exclude-protected-member
            placeholder="Only the Organisation Member role"
          />
        </gaia-form-field>
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="savingRoles" @click="showEditRolesModal = false">
            Cancel
          </gaia-button>
          <gaia-button type="primary" :loading="savingRoles" @click="handleSaveInvitationRoles">
            Save roles
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>
